import React from 'react';
import ContractFullDTO from '../dtos/ContractFullDTO';
import InfoItem from 'features/shared/components/infoItem/InfoItem';
import { formatDate } from 'features/shared/helpers/Formatters';
import { useTranslation } from 'react-i18next';
import PageCard from 'features/shared/components/card/PageCard';
import {getCommodityIcon} from 'features/shared/helpers/Icons';
import ContractEnergyDTO from '../dtos/ContractEnergyDTO';
import { ContractType } from '../enums/contractType';
import ElectricityLowVoltageDTO from '../dtos/ElectricityLowVoltageDTO';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getTerminationDate } from 'features/shared/helpers/dateHelper';
import GasConsumptionBaseDTO from 'features/contracts/dtos/GasConsumptionBaseDTO';

interface Props {
  contract: ContractFullDTO;
  contractEnergy?: ContractEnergyDTO;
  onDistributionInfoClick?: (distributionRate: string) => Promise<void>;
}

const ContractBasicInfoCard = ({contract, contractEnergy, onDistributionInfoClick}: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;
  const tStatus = useTranslation("contracts\\enums\\contractStatus").t;
  const tCategory = useTranslation("contracts\\enums\\contractCategory").t;
  const tCategoryRate = useTranslation("contracts\\enums\\contractCategoryRate").t;
  const tTerms = useTranslation("shared\\terms").t;

  // sub-renders
  const renderElectricityLowVoltageInfo = ():JSX.Element | null => {
    if (contract.contractType == ContractType.ElectricityLowVoltage && contractEnergy && !contract.isProduction) {
      let electricityLowVlotage = contractEnergy as ElectricityLowVoltageDTO;

      return (
        <>
          <div className="w-100"></div>
          <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
            <InfoItem title={tLabels("contractBasicInfoCard.businessRate")} value={electricityLowVlotage.businessRate || ""} icon="chart-pie" />
          </div>
          <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
            <InfoItem title={tLabels("contractBasicInfoCard.distributionRate")} value={renderDistributionRateInfo(electricityLowVlotage.distributionRate)} icon="chart-pie"  />
          </div>
          <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
            <InfoItem title={tLabels("contractBasicInfoCard.phaseCircuitBreaker")} value={`${electricityLowVlotage.phase}x${electricityLowVlotage.circuitBreaker}A`} icon="plug" />
          </div>
        </>
      );
    } else {
      return null;
    }
  }

  const renderDistributionRateInfo = (distributionRate?: string) => {
    if (distributionRate) {
      return (
        <>
          {distributionRate}
          <span role="button" className="ml-1 text-secondary" onClick={onDistributionInfoClick ? () => onDistributionInfoClick(distributionRate) : undefined}>
            <FontAwesomeIcon icon="info-circle"  />
          </span>
        </>
      ); 
    } else {
      return <>-</>;
    }
  }

  const renderSignatureDate = () => { 
    if (contract.signitureDate) {
      return (
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.signitureDate")} value={formatDate(contract.signitureDate)} icon="file-signature" />
        </div>
      );
    }
  }

  const renderTerminationDate = () => { 
    if (contract.terminationDatePresentation) {
      return (
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.terminationDate")} value={getTerminationDate(contract, tTerms)} icon="calendar-times" />
        </div>
      );
    }
  }

  const renderEmergencyGroup = ():JSX.Element | null => {
    let gasContractTypes: ContractType[] = ["GasSmallConsumption", "GasLargeConsumption"];

    if (gasContractTypes.includes(contract.contractType!) && contractEnergy) {
      let gasConsumption = contractEnergy as GasConsumptionBaseDTO;

      if (gasConsumption.emergencyGroup) {
        return (
          <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
            <InfoItem title={tLabels("contractBasicInfoCard.emergencyGroup")} value={gasConsumption.emergencyGroup} icon="exclamation-triangle"  />
          </div>
        )
      }

    } 

    return null;
  }

  const renderPaymentInformation = () => { 
    if (!contract.isProduction) {
      return (
        <>
          <div className="w-100"></div>
          <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
            <InfoItem title={tLabels("contractBasicInfoCard.advanceFrequency")} value={contract.advance?.advanceFrequency || "-"} icon="calendar-alt"  />
          </div>
          <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
            <InfoItem title={tLabels("contractBasicInfoCard.billingCycle")} value={contract.billingCycle || "-"} icon="clock"  />
          </div>
        </>
      );
    }
  }

  // render
  return (
    <PageCard title={tLabels("contractBasicInfoCard.cardTitle")} bodyClassName="pb-0">
      <div className="row no-gutters">
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.contractNumber")} value={contract.contractNumber!} icon={getCommodityIcon(contract.commodityDetail)} />
        </div>
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.contractCategory")} value={contract.contractCategory ? tCategory(contract.contractCategory) : "-"} icon="flag"  />
        </div>
        <div className="col-sm-12 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.contractStatus")} value={tStatus(contract.contractStatus!)} icon="tag" />
        </div>
        {renderSignatureDate()}
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.effectiveDate")} value={formatDate(contract.effectiveDate!)!} icon="calendar-check" />
        </div>
        {renderTerminationDate()}
        <div className="w-100"></div>
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.categoryRate")} value={tCategoryRate(contract.categoryRate!)} icon="file-alt"  />
        </div>
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={tLabels("contractBasicInfoCard.product")} value={contract.product!} icon="shopping-cart"  />
        </div>
        {renderPaymentInformation()}
        {renderEmergencyGroup()}
        {renderElectricityLowVoltageInfo()}      
      </div>
    </PageCard>
  )
}

export default ContractBasicInfoCard;
